import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation } from "react-router-dom";
import { I18nProvider, SUPPORTED, DEFAULT_LANG, useI18n } from "@/i18n/I18nContext";
import ScrollToTop from "@/components/ScrollToTop";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner, { useCookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";
import { Privacy, Cookies, Terms } from "@/pages/Legal";
import ThankYou from "@/pages/ThankYou";

function DocumentHead() {
  const { t, lang } = useI18n();
  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.title = t("meta.title");
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", t("meta.description"));
  }, [lang, t]);
  return null;
}

// Ensures /{lang} route parameter is valid; otherwise redirect to default
function LangGuard({ children }) {
  const { lang } = useParams();
  if (!SUPPORTED.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }
  return children;
}

function Shell({ children, cookieState }) {
  return (
    <>
      <DocumentHead />
      <Header />
      {children}
      <Footer onOpenCookiePrefs={cookieState.open} />
    </>
  );
}

function AppInner() {
  const cookieState = useCookieBanner();
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Root redirects to Croatian */}
        <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />

        {/* Croatian-only thank-you page — no header/footer, no language routing */}
        <Route
          path="/thank_you"
          element={
            <I18nProvider>
              <DocumentHead />
              <ThankYou />
            </I18nProvider>
          }
        />

        <Route
          path="/:lang"
          element={
            <LangGuard>
              <I18nProvider>
                <Shell cookieState={cookieState}>
                  <Home />
                </Shell>
              </I18nProvider>
            </LangGuard>
          }
        />
        <Route
          path="/:lang/privacy"
          element={
            <LangGuard>
              <I18nProvider>
                <Shell cookieState={cookieState}>
                  <Privacy />
                </Shell>
              </I18nProvider>
            </LangGuard>
          }
        />
        <Route
          path="/:lang/cookies"
          element={
            <LangGuard>
              <I18nProvider>
                <Shell cookieState={cookieState}>
                  <Cookies />
                </Shell>
              </I18nProvider>
            </LangGuard>
          }
        />
        <Route
          path="/:lang/terms"
          element={
            <LangGuard>
              <I18nProvider>
                <Shell cookieState={cookieState}>
                  <Terms />
                </Shell>
              </I18nProvider>
            </LangGuard>
          }
        />

        <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
      </Routes>

      {/* Cookie banner is mounted once outside the routed tree so consent state persists */}
      <MountBanner state={cookieState} />
    </>
  );
}

function MountBanner({ state }) {
  // Banner needs i18n; wrap in a tiny provider that also uses current URL to pick language
  return (
    <I18nProvider>
      <CookieBanner state={state} />
    </I18nProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </div>
  );
}
